import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";
import { fetchSearchCount } from "../../src/utils/fetchSearchCount";

// Connects to data-controller="job-filter--modal"
// 求人検索用のモーダル
export default class extends Controller {
  static targets = [
    "modal",
    "selectedText",
    "checkbox",
    "label",
    "hiddenField",
    "regionCheckbox",
    "modalSearchCount",
    "modalSearchCountUnit",
    "confirmButton",
  ];

  declare modalTarget: HTMLElement;
  declare selectedTextTarget: HTMLSpanElement;
  declare checkboxTargets: HTMLInputElement[];
  declare labelTargets: HTMLLabelElement[];
  declare hiddenFieldTarget: HTMLInputElement;
  declare regionCheckboxTargets: HTMLInputElement[];
  declare modalSearchCountTarget: HTMLElement;
  declare modalSearchCountUnitTarget: HTMLElement;
  declare confirmButtonTarget: HTMLButtonElement;

  declare hasRegionCheckboxTarget: boolean;
  declare hasHiddenFieldTarget: boolean;

  modal: Modal;
  debounceTimeout = 0; // デバウンス管理用

  connect() {
    this.modal = new Modal(this.modalTarget);

    // チェックボックスの初期化
    this.updateCheckboxesFromHiddenField();

    const { selectedText } = this.selectTextAndValue();

    // 表示テキストを初期化
    this.selectedTextTarget.textContent = selectedText;

    // 地域チェックボックスが存在する場合のみ、イベントリスナーを追加
    if (this.hasRegionCheckboxTarget) {
      this.setupRegionCheckboxListeners();
    }
  }

  // 業種 change イベント
  handleIndustryChange(_event: Event): void {
    this.fetchModalSearchCountWithDebounce();
  }

  // 職種 change イベント
  handleOccupationChange(_event: Event): void {
    this.fetchModalSearchCountWithDebounce();
  }

  // 勤務地 change イベント
  handleWorkPlaceChange(_event: Event): void {
    this.fetchModalSearchCountWithDebounce();
  }

  openModal(): void {
    // モーダルを開く前にhidden_fieldの値を元にチェックボックスの状態を設定
    this.updateCheckboxesFromHiddenField();

    // 県のチェック状態に基づいて地域チェックボックスを更新
    if (this.hasRegionCheckboxTarget) {
      this.updateRegionCheckboxes();
    }

    this.modal.show();

    // モーダルを開いた時点での検索件数を表示
    this.fetchModalSearchCountWithDebounce();
  }

  closeModal(): void {
    this.modal.hide();
  }

  // 決定ボタンクリック時の処理
  confirm(): void {
    const { selectedText, selectedIds } = this.selectTextAndValue();

    // 表示テキストを更新
    this.selectedTextTarget.textContent = selectedText;
    // hiddenフィールドに選択されたIDをカンマ区切りでセット
    this.hiddenFieldTarget.value = selectedIds.join(",");

    // changeイベントを発火させて件数取得を実行
    this.triggerChangeEvent();

    this.closeModal();
  }

  // hidden_fieldの値からチェックボックスの状態を更新
  updateCheckboxesFromHiddenField(): void {
    const hiddenValue = this.hiddenFieldTarget.value;
    const selectedIds = hiddenValue ? hiddenValue.split(",") : [];

    // すべてのチェックボックスをリセット
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = selectedIds.includes(checkbox.value);
    });

    // 県のチェック状態に基づいて地域チェックボックスを更新
    if (this.hasRegionCheckboxTarget) {
      this.updateRegionCheckboxes();
    }
  }

  // チェックボックスの状態に応じて表示テキストとhiddenフィールドの値を取得
  selectTextAndValue(): { selectedText: string; selectedIds: string[] } {
    const checkedBoxes = this.checkboxTargets.filter((checkbox) => checkbox.checked);
    let selectedText = "指定なし";

    const selectedIds: string[] = [];

    if (checkedBoxes.length !== 0) {
      const selectedNames: string[] = [];

      checkedBoxes.forEach((checkbox) => {
        // 選択されたチェックボックスのvalue（ID）を取得して配列に追加
        selectedIds.push(checkbox.value);

        // 対応するラベルを探す (idとforの関係を利用)
        const labelIndex = this.checkboxTargets.indexOf(checkbox);
        const label = this.labelTargets[labelIndex];

        if (label) {
          // spanを除いたテキストだけを取得（firstChildがテキストノード）
          const labelText = label.firstChild?.textContent?.trim();
          selectedNames.push(labelText || "");
        }
      });

      selectedText = selectedNames.join(" / ");
    }

    return { selectedText, selectedIds };
  }

  // 地域チェックボックスのイベントリスナーをセットアップ
  setupRegionCheckboxListeners(): void {
    this.regionCheckboxTargets.forEach((regionCheckbox) => {
      regionCheckbox.addEventListener("change", (event) => {
        const target = event.target as HTMLInputElement;
        const regionId = target.value;
        const isChecked = target.checked;

        // 地域に属する県のチェックボックスを取得
        const prefectureCheckboxes = this.checkboxTargets.filter((checkbox) => checkbox.dataset.regionId === regionId);

        // 県のチェックボックスの状態を更新
        prefectureCheckboxes.forEach((checkbox) => {
          checkbox.checked = isChecked;
        });
      });
    });

    // 県のチェックボックスにイベントリスナーを追加
    if (this.hasRegionCheckboxTarget) {
      this.checkboxTargets.forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
          this.updateRegionCheckboxes();
        });
      });
    }
  }

  // 各地域チェックボックスの状態を、所属する県のチェック状態に基づいて更新
  updateRegionCheckboxes(): void {
    // Stimulusのターゲットを使用して地域チェックボックスを処理
    this.regionCheckboxTargets.forEach((regionCheckbox) => {
      const regionId = regionCheckbox.value;

      // この地域に属する全ての県チェックボックスを取得
      const prefectureCheckboxes = this.checkboxTargets.filter((checkbox) => checkbox.dataset.regionId === regionId);

      if (prefectureCheckboxes.length === 0) return;

      // 全ての県がチェックされているかどうか確認
      const allChecked = prefectureCheckboxes.every((checkbox) => checkbox.checked);

      // 全ての県がチェックされている場合は地域もチェック, そうでない場合は地域のチェックを外す
      regionCheckbox.checked = allChecked;
    });
  }

  // リセットボタンの処理
  reset(): void {
    // すべてのチェックボックスの選択を解除
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // 地域のチェックボックスも選択解除
    if (this.hasRegionCheckboxTarget) {
      this.regionCheckboxTargets.forEach((regionCheckbox) => {
        regionCheckbox.checked = false;
      });
    }

    // リセット後に検索件数を更新
    this.fetchModalSearchCountWithDebounce();
  }

  // hiddenフィールドのchangeイベントを発火させる
  private triggerChangeEvent(): void {
    const event = new Event("change", { bubbles: true });
    this.hiddenFieldTarget.dispatchEvent(event);
  }

  // 検索件数を取得する(デバウンスあり)
  private fetchModalSearchCountWithDebounce() {
    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = window.setTimeout(async () => {
      // 親フォームからデータを取得（IDで直接取得）
      // PC or SP のどちらかのフォームを取得
      const parentForm =
        (document.getElementById("sidebarSearchForm") as HTMLFormElement) ||
        (document.getElementById("spSearchForm") as HTMLFormElement);

      // モーダルのformの値を取得
      const { selectedIds } = this.selectTextAndValue();

      const count = await fetchSearchCount({
        form: parentForm,
        overrideParams: { keys: [this.hiddenFieldTarget.name], values: [selectedIds.join(",")] },
      });
      this.updateModalSearchCount(Number(count));
      this.confirmButtonTarget.disabled = Number(count) <= 0;
    }, 500);
  }

  // モーダル内の検索件数表示を更新する
  private updateModalSearchCount(count: number): void {
    if (count > 999) {
      this.modalSearchCountTarget.textContent = "999";
      this.modalSearchCountUnitTarget.textContent = "件以上";
    } else {
      this.modalSearchCountTarget.textContent = count.toString();
      this.modalSearchCountUnitTarget.textContent = "件";
    }
  }
}
